import { Action, CachedFunction } from '@solidjs/router';

import { ApiPaths as MatchApiPaths, type paths as matchPaths, type components as matchComponents } from './match/api';
import { ApiPaths as LoginApiPaths, type paths as loginPaths, type components as loginComponents } from './login/api';
import {
  ApiPaths as WalletApiPaths,
  type paths as walletPaths,
  type components as walletComponents,
} from './wallet/api';
import { ApiPaths as OddsApiPaths, type paths as OddsPaths, type components as OddsComponents } from './odds/api';
import {
  ApiPaths as RecommandApiPaths,
  type paths as RecommandPaths,
  type components as RecommandComponents,
} from './recommand/api';
import { ApiPaths as OrderApiPaths, type paths as OrderPaths, type components as OrderComponents } from './order/api';
import {
  ApiPaths as GatewayApiPaths,
  type paths as GatewayPaths,
  type components as GatewayComponents,
} from './gateway/api';
import {
  ApiPaths as SportDataApiPaths,
  type paths as SportDataPaths,
  type components as SportDataComponents,
} from './sportdata/api';
import {
  ApiPaths as PromotionApiPaths,
  type paths as PromotionPaths,
  type components as PromotionComponents,
} from './promotion/api';

export interface paths
  extends
    matchPaths,
    loginPaths,
    walletPaths,
    OddsPaths,
    RecommandPaths,
    GatewayPaths,
    OrderPaths,
    SportDataPaths,
    PromotionPaths {}

export type ApiSchemas = matchComponents['schemas'] &
  loginComponents['schemas'] &
  walletComponents['schemas'] &
  OddsComponents['schemas'] &
  RecommandComponents['schemas'] &
  OrderComponents['schemas'] &
  GatewayComponents['schemas'] &
  SportDataComponents['schemas'] &
  PromotionComponents['schemas'];

export const ApiPaths = {
  ...MatchApiPaths,
  ...LoginApiPaths,
  ...WalletApiPaths,
  ...OddsApiPaths,
  ...RecommandApiPaths,
  ...OrderApiPaths,
  ...GatewayApiPaths,
  ...SportDataApiPaths,
  ...PromotionApiPaths,
};

export * from './match/api-types';
export * from './odds/api-types';
export * from './sportdata/api-types';
export * from './recommand/api-types';

type ApiPathsType = typeof ApiPaths;

type GetRes<T> = {
  responses: { 200: { content?: { '*/*': { data: T } } } };
};
type GetBody<T> = {
  requestBody: {
    content: {
      'application/json': T;
    };
  };
};
type GetPath<T> = {
  parameters: {
    path: T;
  };
};

type GetQuery<T> = {
  parameters: {
    query?: T;
  };
};

type Test<T> = {
  get?: T;
  post?: T;
};

export type ApiResponse<T extends keyof ApiPathsType> =
  paths[ApiPathsType[T]] extends Test<GetRes<infer R>> ? R : never;
export type ApiBody<T extends keyof ApiPathsType> = paths[ApiPathsType[T]] extends Test<GetBody<infer R>> ? R : never;
export type ApiQuery<T extends keyof ApiPathsType> = paths[ApiPathsType[T]] extends Test<GetQuery<infer R>> ? R : never;
export type ApiPath<T extends keyof ApiPathsType> = paths[ApiPathsType[T]] extends Test<GetPath<infer R>> ? R : never;

type HasPath<T extends Methods> = {
  [K in keyof ApiPathsType]: T extends keyof paths[ApiPathsType[K]]
    ? undefined extends paths[ApiPathsType[K]][T]
      ? never
      : K
    : never;
}[keyof ApiPathsType];

type Methods = 'get' | 'post';
export type ApiGetKeys = HasPath<'get'>;
export type ApiPostKeys = HasPath<'post'>;

type IsEmpty<T> = [T] extends [never | undefined] ? true : false;

type Merge<A, B> =
  IsEmpty<A> extends true ? (IsEmpty<B> extends true ? [] : [B]) : IsEmpty<B> extends true ? [A] : [A, B];

export type GetApis = {
  [K in ApiGetKeys]: CachedFunction<(...args: Merge<ApiQuery<K>, ApiPath<K>>) => Promise<ApiResponse<K>>>;
};

export type PostApis = {
  [K in ApiPostKeys]: Action<Merge<ApiBody<K>, ApiPath<K>>, ApiResponse<K>>;
};
